import React, { useMemo } from 'react';
import { ListPage } from '../../../components/crud/ListPage';
import { DataTable } from '../../../components/ui/data-table';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { useTable } from '@refinedev/core';
import { useNavigate } from 'react-router-dom';
import { Radiation, Eye, Edit, CheckCircle, XCircle, Image, Clock } from 'lucide-react';
import type { ColumnDef } from '@tanstack/react-table';

const statusMap: Record<string, string> = { active: 'success', inactive: 'default', maintenance: 'warning', error: 'error', offline: 'error' };

export const ImagingDevicesList: React.FC = () => {
  const navigate = useNavigate();
  const { tableQueryResult: { data: tableData, isLoading } } = useTable({ resource: 'devices/imaging', syncWithLocation: false });
  const devices = useMemo(() => (tableData?.data || []) as any[], [tableData]);

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'name', header: 'Imaging Device',
      cell: ({ row }) => (
        <div>
          <div className="flex items-center gap-2"><Radiation className="h-4 w-4 text-muted-foreground" /><span className="font-medium">{row.original.name}</span></div>
          <div className="text-xs text-muted-foreground">{row.original.manufacturer} {row.original.model}</div>
          <div className="text-[11px] text-muted-foreground">S/N: {row.original.serial_number}</div>
        </div>
      ),
    },
    {
      id: 'modality', header: 'Modality & Location',
      cell: ({ row }) => (
        <div>
          <Badge variant="info" className="mb-1">{row.original.modality_display || row.original.modality}</Badge>
          <div className="text-sm">{row.original.category}</div>
          <div className="text-xs text-muted-foreground">via {row.original.connector_name}</div>
        </div>
      ),
    },
    {
      id: 'dicom', header: 'DICOM / Network',
      cell: ({ row }) => (
        <div>
          <div className="flex items-center gap-1 text-xs mb-1">
            {row.original.dicom_enabled ? <><CheckCircle className="h-3 w-3 text-success-700" /> DICOM</> : <><XCircle className="h-3 w-3 text-muted-foreground" /> Non-DICOM</>}
          </div>
          {row.original.ae_title && <code className="text-[10px] bg-muted px-1 rounded">AE: {row.original.ae_title}</code>}
          {row.original.device_ip && <div><code className="text-[10px] bg-muted px-1 rounded">{row.original.device_ip}:{row.original.device_port}</code></div>}
        </div>
      ),
    },
    {
      accessorKey: 'status', header: 'Status',
      cell: ({ row }) => <Badge variant={(statusMap[row.original.status] || 'default') as any}>{row.original.status_display}</Badge>,
    },
    {
      id: 'studies', header: 'Studies',
      cell: ({ row }) => (
        <div className="text-xs space-y-0.5">
          <div className="flex items-center gap-1"><Image className="h-3 w-3" />{(row.original.study_count ?? 0).toLocaleString()} studies</div>
          <div className="flex items-center gap-1 text-muted-foreground"><Clock className="h-3 w-3" />{row.original.last_image_received ? new Date(row.original.last_image_received).toLocaleDateString() : 'No images'}</div>
        </div>
      ),
    },
    {
      id: 'actions', header: '',
      cell: ({ row }) => (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/devices/imaging/show/${row.original.id}`)}><Eye className="h-4 w-4" /></Button>
          <Button variant="ghost" size="icon" onClick={() => navigate(`/devices/imaging/edit/${row.original.id}`)}><Edit className="h-4 w-4" /></Button>
        </div>
      ),
    },
  ];

  return (
    <ListPage title="Imaging Devices (PACS)" createButtonProps={{ onClick: () => navigate('/devices/imaging/create'), children: 'Add Imaging Device' }}>
      <DataTable columns={columns} data={devices} isLoading={isLoading} searchKey="name" emptyMessage="No imaging devices found" onRowClick={(row) => navigate(`/devices/imaging/edit/${row.id}`)} />
    </ListPage>
  );
};

export default ImagingDevicesList;
